// components/forms/teamForms/editBrand.js (or EditBrandForm.jsx)
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from '../formComponents/ErrorMessage';
import SubmitButton from '../formComponents/SubmitButton';
import { ref, uploadBytes as nativeUploadBytes, getDownloadURL as nativeGetDownloadURL, listAll as nativeListAll, deleteObject as nativeDeleteObject } from 'firebase/storage';
import { storage } from '../../../firebase-client';
import '../../../index.css';
import './editBrand.css';
export default function EditBrandForm({
  initialBrand,
  initialClubUrlSlug = '',
  editClubUrl = false,
  isOpen,
  onClose,
  onSave,
  teamId,
  dataAccess = null,
}) {
  const { uploadBytes, getDownloadURL, listAll, deleteObject } = dataAccess || { uploadBytes: nativeUploadBytes, getDownloadURL: nativeGetDownloadURL, listAll: nativeListAll, deleteObject: nativeDeleteObject };
  const brand = initialBrand ?? {};
  const [clubUrlSlug, setClubUrlSlug] = useState(initialClubUrlSlug);
  const [logoUrl, setLogoUrl] = useState(brand.logoUrl || '');
  const [primaryColor, setPrimaryColor] = useState(brand.primaryColor || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(brand.secondaryColor || '#f8f9fa');
  const [headingTextColor, setHeadingTextColor] = useState(brand.headingTextColor || '#333333');
  const [bodyTextColor, setBodyTextColor] = useState(brand.bodyTextColor || '#6c757d');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  if (!isOpen) return null;
  const handleLogoUpload = async file => {
    setUploading(true);
    setSubmitting(true);
    setError(null);
    try {
      const ext = typeof file?.name === 'string' ? file.name.split('.').pop()?.toLowerCase() : '';
      const folderRef = ref(storage, `logos/${teamId}`);

      // Wipe existing files in the folder
      const existing = await listAll(folderRef);
      await Promise.all(existing.items.map(fileRef => deleteObject(fileRef)));

      // Upload new logo
      const logoRef = ref(storage, `logos/${teamId}/logo.${ext || 'png'}`);
      await uploadBytes(logoRef, file, {
        contentType: file.type,
        cacheControl: 'no-cache'
      });
      const url = await getDownloadURL(logoRef);
      setLogoUrl(url);
    } catch (err) {
      setError('Logo upload failed');
    } finally {
      setUploading(false);
      setSubmitting(false);
    }
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const nextBrand = { logoUrl, primaryColor, secondaryColor, headingTextColor, bodyTextColor };
      await onSave(editClubUrl
        ? { clubUrlSlug: clubUrlSlug.trim().toLowerCase(), brand: nextBrand }
        : nextBrand);
    } catch (err) {
      setError(err?.message || 'Failed to update branding');
    } finally {
      setSubmitting(false);
    }
  };
  const colours = [
    { id: 'primaryColor', label: 'Primary Colour', hint: 'Headers and buttons', value: primaryColor, set: setPrimaryColor },
    { id: 'secondaryColor', label: 'Secondary Colour', hint: 'Card backgrounds', value: secondaryColor, set: setSecondaryColor },
    { id: 'headingColor', label: 'Text Heading Colour', hint: 'Text on your primary colour', value: headingTextColor, set: setHeadingTextColor },
    { id: 'bodyColor', label: 'Body Text Colour', hint: 'Main content', value: bodyTextColor, set: setBodyTextColor },
  ];
  return <div className="branding-editor">
    <header className="branding-editor__header">
      <h2 className="modal-title">Edit Branding</h2>
      <p>{editClubUrl ? 'Manage your logo, colours and club address.' : 'Manage your club logo and colours.'}</p>
    </header>
    {error && <div className="branding-editor__error"><ErrorMessage message={error} /></div>}
    <form onSubmit={handleSubmit} className="branding-editor__form">
      <div className="branding-editor__body">
        <section className="branding-editor__section" aria-labelledby="brandIdentityHeading">
          <h3 id="brandIdentityHeading">Club identity</h3>
          <div className="branding-editor__logo-row">
            <div className="branding-editor__logo">
              {logoUrl ? <img src={logoUrl} alt="Logo preview" /> : <span>No logo yet</span>}
            </div>
            <div className="branding-editor__logo-details">
              <strong>Club logo</strong>
              <p>Used on your club cards and pages.</p>
              <label className="branding-editor__upload" htmlFor="logoUpload">
                {uploading ? 'Uploading…' : logoUrl ? 'Change logo' : 'Upload logo'}
                <input type="file" accept="image/*" id="logoUpload" aria-label="Upload Logo"
                  onChange={e => { const file = e.target.files?.[0]; if (file) handleLogoUpload(file); }} disabled={submitting} />
              </label>
            </div>
          </div>
          {editClubUrl && <div className="branding-editor__url">
            <label className="form-label" htmlFor="clubUrlSlug">Club URL</label>
            <div className="branding-editor__url-input">
              <span aria-hidden="true">/teams/</span>
              <input type="text" id="clubUrlSlug" value={clubUrlSlug}
                onChange={e => setClubUrlSlug(e.target.value.toLowerCase())}
                placeholder="your-club-name" minLength={3} maxLength={64}
                pattern="[a-z0-9]+(-[a-z0-9]+)*" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                aria-describedby={clubUrlSlug ? 'clubUrlHelp clubUrlPreview' : 'clubUrlHelp'} disabled={submitting} />
            </div>
            <p id="clubUrlHelp" className="branding-editor__hint">3–64 letters, numbers or hyphens. Optional; old links keep working.</p>
            {clubUrlSlug && <p id="clubUrlPreview" className="branding-editor__url-preview">
              {window.location.origin}/teams/{clubUrlSlug}
            </p>}
          </div>}
        </section>
        <section className="branding-editor__section" aria-labelledby="brandColoursHeading">
          <h3 id="brandColoursHeading">Club colours</h3>
          <div className="branding-editor__colours">
            {colours.map(colour => <div className="branding-editor__colour" key={colour.id}>
              <input type="color" id={colour.id} value={colour.value} onChange={e => colour.set(e.target.value)} disabled={submitting} />
              <div>
                <label htmlFor={colour.id}>{colour.label}</label>
                <span>{colour.hint}</span>
                <code>{colour.value.toUpperCase()}</code>
              </div>
            </div>)}
          </div>
        </section>
        <section className="branding-editor__section branding-editor__preview-section" aria-labelledby="brandPreviewHeading">
          <h3 id="brandPreviewHeading">Live preview</h3>
          <div className="branding-editor__preview" style={{ backgroundColor: secondaryColor, color: bodyTextColor }}>
            <div className="branding-editor__preview-header" style={{ backgroundColor: primaryColor, color: headingTextColor }}>
              {logoUrl && <img src={logoUrl} alt="" />}
              <strong>Your club</strong>
            </div>
            <div className="branding-editor__preview-body">
              <p>A place for your members.</p>
              <span style={{ backgroundColor: primaryColor, color: headingTextColor }}>Club updates</span>
            </div>
          </div>
        </section>
      </div>
      <div className="form-actions branding-editor__footer">
        <SubmitButton type="button" label="Cancel" onClick={onClose} disabled={submitting} variant="ghost" size="sm" />
        <SubmitButton type="submit" label={submitting ? (uploading ? 'Uploading…' : 'Saving…') : 'Save'} disabled={submitting} variant="primary" size="sm" />
      </div>
    </form>
  </div>;
}

EditBrandForm.propTypes = {
  initialBrand: PropTypes.object.isRequired,
  initialClubUrlSlug: PropTypes.string,
  editClubUrl: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  teamId: PropTypes.string.isRequired
};
